import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getFirestore, collection, addDoc, serverTimestamp, doc, setDoc, updateDoc, increment } from 'firebase/firestore';
import app from '../firebase/config';
import { subscriptionService } from './subscriptionService';

// Initialize Firebase Analytics and Firestore
let analytics = null;
let db = null;

try {
    analytics = getAnalytics(app);
    db = getFirestore(app);
} catch (error) {
    console.warn('Firebase Analytics or Firestore not available:', error);
}

/**
 * Usage Tracking Service
 * Tracks user actions using Firebase Analytics and Firestore
 */
class UsageTrackingService {
    constructor() {
        this.currentUser = null;
        this.sessionStartTime = null;
    }

    /**
     * Initialize tracking for a user
     * @param {Object} user - Firebase user object
     */
    initializeUser(user) {
        if (!user) return;

        this.currentUser = user;
        this.sessionStartTime = Date.now();

        // Set user ID in Analytics (if available)
        if (analytics) {
            setUserId(analytics, user.uid);
            setUserProperties(analytics, {
                email: user.email,
                created_at: user.metadata.creationTime,
                last_sign_in: user.metadata.lastSignInTime
            });
        }

        // Log session start
        this.trackEvent('session_start', {
            timestamp: new Date().toISOString()
        });

        // Update user document in Firestore
        this.updateUserDocument(user);
    }

    /**
     * Update user document with latest session info
     * @param {Object} user - Firebase user object
     */
    async updateUserDocument(user) {
        if (!db || !user) return;

        try {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, {
                email: user.email,
                lastLoginAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error('Error updating user document:', error);
        }
    }

    /**
     * Track a custom event
     * @param {string} eventName - Name of the event
     * @param {Object} eventParams - Additional parameters for the event
     */
    trackEvent(eventName, eventParams = {}) {
        if (!analytics || !this.currentUser) return;

        const enrichedParams = {
            ...eventParams,
            user_id: this.currentUser.uid,
            timestamp: new Date().toISOString()
        };

        // Log to Firebase Analytics
        logEvent(analytics, eventName, enrichedParams);

        // Also log to Firestore for detailed tracking
        this.logToFirestore(eventName, enrichedParams);
    }

    /**
     * Log event to Firestore
     * Note: Simplified - we no longer write to separate collections.
     * Usage is tracked via usageCount in users collection.
     * Firebase Analytics handles event tracking.
     */
    async logToFirestore(eventName, eventParams) {
        // No-op: simplified architecture doesn't use separate collections
        return;
    }

    /**
     * Track video loaded
     * @param {string} videoSource - 'url' or 'file'
     * @param {string} filename - Video filename
     */
    trackVideoLoaded(videoSource, filename = '') {
        this.trackEvent('video_loaded', {
            source: videoSource,
            filename: filename
        });
    }

    /**
     * Track annotation created
     * @param {string} annotationType - Type of annotation (ball, player, court)
     * @param {string} mode - Annotation mode (stroke, bounce, etc.)
     * @returns {Promise<Object>} Result with success status and usage info
     */
    async trackAnnotationCreated(annotationType, mode = '') {
        // First check if user can create annotations
        if (!this.currentUser) {
            return {
                success: false,
                error: 'User not authenticated',
                canContinue: false
            };
        }

        try {
            // Check subscription and increment usage
            const usageResult = await subscriptionService.incrementUsage(this.currentUser.uid, 1);

            // Track the event
            this.trackEvent('annotation_created', {
                type: annotationType,
                mode: mode,
                usageCount: usageResult.usageCount
            });

            return {
                success: true,
                canContinue: true,
                usage: usageResult
            };
        } catch (error) {
            console.error('Error tracking annotation:', error);

            // Check if it's a usage limit error
            if (error.message.includes('Usage limit exceeded') ||
                error.message.includes('subscription inactive')) {
                return {
                    success: false,
                    error: error.message,
                    canContinue: false,
                    needsUpgrade: true
                };
            }

            // For other errors, still track the event but warn
            this.trackEvent('annotation_created', {
                type: annotationType,
                mode: mode,
                error: error.message
            });

            return {
                success: false,
                error: error.message,
                canContinue: true // Allow continuing for non-billing errors
            };
        }
    }

    /**
     * Track annotation deleted
     * @param {string} annotationType - Type of annotation
     */
    trackAnnotationDeleted(annotationType) {
        this.trackEvent('annotation_deleted', {
            type: annotationType
        });
    }

    /**
     * Track annotations saved
     * @param {number} ballCount - Number of ball annotations
     * @param {number} playerCount - Number of player annotations
     * @param {number} courtCount - Number of court keypoints
     */
    trackAnnotationsSaved(ballCount, playerCount, courtCount) {
        this.trackEvent('annotations_saved', {
            ball_annotations: ballCount,
            player_annotations: playerCount,
            court_keypoints: courtCount,
            total_annotations: ballCount + playerCount + courtCount
        });
    }

    /**
     * Track annotations loaded
     * @param {number} ballCount - Number of ball annotations
     * @param {number} playerCount - Number of player annotations
     * @param {number} courtCount - Number of court keypoints
     */
    trackAnnotationsLoaded(ballCount, playerCount, courtCount) {
        this.trackEvent('annotations_loaded', {
            ball_annotations: ballCount,
            player_annotations: playerCount,
            court_keypoints: courtCount,
            total_annotations: ballCount + playerCount + courtCount
        });
    }

    /**
     * Track API call
     * @param {string} apiName - Name of the API (player_detector, keypoint_detector, etc.)
     * @param {boolean} success - Whether the call was successful
     * @param {number} duration - Duration in milliseconds
     */
    trackApiCall(apiName, success, duration = 0) {
        this.trackEvent('api_call', {
            api_name: apiName,
            success: success,
            duration_ms: duration
        });
    }

    /**
     * Track camera calibration
     * @param {string} trigger - How calibration was triggered (auto, manual)
     * @param {boolean} success - Whether calibration was successful
     */
    trackCameraCalibration(trigger, success) {
        this.trackEvent('camera_calibration', {
            trigger: trigger,
            success: success
        });
    }

    /**
     * Track player detection
     * @param {number} playersDetected - Number of players detected
     * @param {boolean} automatic - Whether detection was automatic
     * @returns {Promise<Object>} Result with success status and usage info
     */
    async trackPlayerDetection(playersDetected, automatic = true) {
        // Player detections count toward usage
        if (!this.currentUser) {
            return {
                success: false,
                error: 'User not authenticated',
                canContinue: false
            };
        }

        try {
            // Increment usage by number of players detected
            const usageResult = await subscriptionService.incrementUsage(
                this.currentUser.uid,
                playersDetected
            );

            this.trackEvent('player_detection', {
                players_detected: playersDetected,
                automatic: automatic,
                usageCount: usageResult.usageCount
            });

            return {
                success: true,
                canContinue: true,
                usage: usageResult
            };
        } catch (error) {
            console.error('Error tracking player detection:', error);

            if (error.message.includes('Usage limit exceeded') ||
                error.message.includes('subscription inactive')) {
                return {
                    success: false,
                    error: error.message,
                    canContinue: false,
                    needsUpgrade: true
                };
            }

            this.trackEvent('player_detection', {
                players_detected: playersDetected,
                automatic: automatic,
                error: error.message
            });

            return {
                success: false,
                error: error.message,
                canContinue: true
            };
        }
    }

    /**
     * Track court prediction
     * @param {boolean} success - Whether prediction was successful
     */
    trackCourtPrediction(success) {
        this.trackEvent('court_prediction', {
            success: success
        });
    }

    /**
     * Track mode switch
     * @param {string} fromMode - Previous mode
     * @param {string} toMode - New mode
     */
    trackModeSwitch(fromMode, toMode) {
        this.trackEvent('mode_switch', {
            from_mode: fromMode,
            to_mode: toMode
        });
    }

    /**
     * Track tutorial interaction
     * @param {string} action - Tutorial action (start, complete, skip, step)
     * @param {number} stepNumber - Current step number (if applicable)
     */
    trackTutorial(action, stepNumber = null) {
        this.trackEvent('tutorial_interaction', {
            action: action,
            step_number: stepNumber
        });
    }

    /**
     * Track feedback submission
     * @param {string} feedbackType - Type of feedback
     */
    trackFeedbackSubmission(feedbackType) {
        this.trackEvent('feedback_submitted', {
            feedback_type: feedbackType
        });
    }

    /**
     * Check if user can send video for processing (without incrementing counter)
     * @returns {Promise<Object>} Result with canContinue status
     */
    async checkVideoProcessingAllowance() {
        if (!this.currentUser) {
            return {
                canContinue: false,
                error: 'User not authenticated'
            };
        }

        try {
            const subscription = await subscriptionService.getUserSubscription(this.currentUser.uid);

            if (!subscription) {
                return {
                    canContinue: false,
                    error: 'No subscription found',
                    needsUpgrade: true
                };
            }

            const remaining = subscription.videoProcessingRemaining;

            if (remaining !== null && remaining <= 0) {
                return {
                    canContinue: false,
                    error: 'Video processing limit reached',
                    needsUpgrade: true
                };
            }

            return {
                canContinue: true,
                remaining: remaining
            };
        } catch (error) {
            console.error('Error checking video processing allowance:', error);
            return {
                canContinue: false,
                error: error.message
            };
        }
    }

    /**
     * Track video sent for processing (call AFTER successful upload)
     * @param {string} videoName - Name of the video being processed
     * @returns {Promise<Object>} Result with success status and usage info
     */
    async trackVideoSentForProcessing(videoName) {
        if (!this.currentUser) {
            return {
                success: false,
                error: 'User not authenticated',
                canContinue: false
            };
        }

        try {
            // Increment video processing usage via server API (prevents client tampering)
            const idToken = await this.currentUser.getIdToken();
            const response = await fetch('/api/record-video-processing', {
                method: 'POST',
                headers: {
                    'X-Firebase-Token': idToken,
                    'Content-Type': 'application/json',
                },
            });
            const usageResult = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (response.status === 403 && usageResult.needsUpgrade) {
                    return {
                        success: false,
                        error: usageResult.error || 'Video processing limit reached',
                        canContinue: false,
                        needsUpgrade: true
                    };
                }
                throw new Error(usageResult.error || `Request failed (${response.status})`);
            }

            this.trackEvent('video_sent_for_processing', {
                video_name: videoName,
                videoProcessingCount: usageResult.videoProcessingCount
            });

            return {
                success: true,
                canContinue: true,
                usage: usageResult
            };
        } catch (error) {
            console.error('Error tracking video processing:', error);

            if (error.message.includes('limit') ||
                error.message.includes('inactive') ||
                error.message.includes('expired')) {
                return {
                    success: false,
                    error: error.message,
                    canContinue: false,
                    needsUpgrade: true
                };
            }

            this.trackEvent('video_sent_for_processing', {
                video_name: videoName,
                error: error.message
            });

            return {
                success: false,
                error: error.message,
                canContinue: false
            };
        }
    }

    /**
     * Track Azure storage interaction
     * @param {string} action - Action performed (upload, download, list)
     * @param {boolean} success - Whether action was successful
     */
    trackAzureStorage(action, success) {
        this.trackEvent('azure_storage', {
            action: action,
            success: success
        });
    }

    /**
     * Track session end
     */
    trackSessionEnd() {
        if (!this.sessionStartTime) return;

        const sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);

        this.trackEvent('session_end', {
            duration_seconds: sessionDuration,
            timestamp: new Date().toISOString()
        });
    }

    /**
     * Check if user can use the service
     * @returns {Promise<Object>} Object with canUse boolean and subscription details
     */
    async checkUsageAllowance() {
        if (!this.currentUser) {
            return {
                canUse: false,
                error: 'User not authenticated'
            };
        }

        try {
            const subscription = await subscriptionService.getUserSubscription(this.currentUser.uid);

            if (!subscription) {
                return {
                    canUse: false,
                    error: 'No subscription found',
                    needsUpgrade: true
                };
            }

            return {
                canUse: subscription.canUseService,
                subscription: subscription,
                needsUpgrade: !subscription.canUseService
            };
        } catch (error) {
            console.error('Error checking usage allowance:', error);
            return {
                canUse: false,
                error: error.message
            };
        }
    }

    /**
     * Clear user data on logout
     */
    clearUser() {
        if (this.currentUser) {
            this.trackSessionEnd();
        }
        this.currentUser = null;
        this.sessionStartTime = null;
    }
}

// Export singleton instance
export const usageTrackingService = new UsageTrackingService();

export default usageTrackingService;

